import type { AxiosResponse } from "axios";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { api } from "../api";
import {
  type DelayedJobRecord,
  type DelayedJobsState,
  EMPTY_OVERVIEW,
  EMPTY_PAGINATION,
  type ListPayload,
  type OverviewPayload,
  type RootState,
  type TabId,
  TABS,
  buildListParams,
  buildQueueParams
} from "../types";

export const CHANGE_TAB = "delayedJobs/CHANGE_TAB" as const;
export const SET_QUEUES_INPUT = "delayedJobs/SET_QUEUES_INPUT" as const;
export const SET_JOB_CLASS_INPUT = "delayedJobs/SET_JOB_CLASS_INPUT" as const;
export const APPLY_FILTERS = "delayedJobs/APPLY_FILTERS" as const;
export const CHANGE_PAGE = "delayedJobs/CHANGE_PAGE" as const;
export const REFRESH = "delayedJobs/REFRESH" as const;
export const FETCH_DATA_SUCCESS = "delayedJobs/FETCH_DATA_SUCCESS" as const;
export const FETCH_JOB = "delayedJobs/FETCH_JOB" as const;
export const FETCH_JOB_SUCCESS = "delayedJobs/FETCH_JOB_SUCCESS" as const;
export const CLOSE_JOB = "delayedJobs/CLOSE_JOB" as const;
export const REQUEUE_JOB = "delayedJobs/REQUEUE_JOB" as const;
export const RELOAD_JOB = "delayedJobs/RELOAD_JOB" as const;
export const DESTROY_JOB = "delayedJobs/DESTROY_JOB" as const;
export const REQUEUE_FAILED = "delayedJobs/REQUEUE_FAILED" as const;
export const REQUEUE_PENDING = "delayedJobs/REQUEUE_PENDING" as const;
export const CLEAR_FAILED = "delayedJobs/CLEAR_FAILED" as const;
export const SET_LOADING = "delayedJobs/SET_LOADING" as const;
export const SET_ACTION_PENDING = "delayedJobs/SET_ACTION_PENDING" as const;
export const SET_ERROR = "delayedJobs/SET_ERROR" as const;

export const changeTab = (tab: TabId) => ({ type: CHANGE_TAB, payload: tab });
export const setQueuesInput = (value: string) => ({ type: SET_QUEUES_INPUT, payload: value });
export const setJobClassInput = (value: string) => ({ type: SET_JOB_CLASS_INPUT, payload: value });
export const applyFilters = () => ({ type: APPLY_FILTERS });
export const changePage = (page: number) => ({ type: CHANGE_PAGE, payload: page });
export const refresh = () => ({ type: REFRESH });
export const fetchJob = (id: number) => ({ type: FETCH_JOB, payload: id });
export const closeJob = () => ({ type: CLOSE_JOB });
export const requeueJob = (id: number) => ({ type: REQUEUE_JOB, payload: id });
export const reloadJob = (id: number) => ({ type: RELOAD_JOB, payload: id });
export const destroyJob = (id: number) => ({ type: DESTROY_JOB, payload: id });
export const requeueFailed = () => ({ type: REQUEUE_FAILED });
export const requeuePending = () => ({ type: REQUEUE_PENDING });
export const clearFailed = () => ({ type: CLEAR_FAILED });

function extractError(error: unknown): string {
  const axiosError = error as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };
  return (
    axiosError.response?.data?.error ||
    axiosError.response?.data?.message ||
    axiosError.message ||
    "Request failed"
  );
}

function* selectModuleState() {
  const state: DelayedJobsState = yield select((root: RootState) => root.delayedJobs);
  return state;
}

function* fetchData() {
  const { activeTab, appliedQueues, appliedJobClass, page }: DelayedJobsState =
    yield selectModuleState();

  yield put({ type: SET_LOADING, payload: true });
  yield put({ type: SET_ERROR, payload: null });

  try {
    const overviewResponse: AxiosResponse<OverviewPayload> = yield call(
      api.getOverview,
      buildQueueParams(appliedQueues)
    );
    const tab = TABS.find((item) => item.id === activeTab);
    let records: DelayedJobRecord[] = [];
    let pagination = EMPTY_PAGINATION;

    if (tab?.listStatus) {
      const listResponse: AxiosResponse<ListPayload> = yield call(
        api.getList,
        buildListParams({
          status: tab.listStatus,
          page,
          queues: appliedQueues,
          jobClass: appliedJobClass
        })
      );
      records = listResponse.data?.records || [];
      pagination = listResponse.data?.pagination || EMPTY_PAGINATION;
    }

    yield put({
      type: FETCH_DATA_SUCCESS,
      payload: {
        overview: overviewResponse.data || EMPTY_OVERVIEW,
        records,
        pagination
      }
    });
  } catch (error) {
    yield put({ type: SET_ERROR, payload: extractError(error) });
  } finally {
    yield put({ type: SET_LOADING, payload: false });
    yield put({ type: SET_ACTION_PENDING, payload: false });
  }
}

function* fetchJobSaga(action: { payload: number }) {
  yield put({ type: SET_ERROR, payload: null });
  try {
    const response: AxiosResponse<DelayedJobRecord> = yield call(api.getJob, action.payload);
    yield put({ type: FETCH_JOB_SUCCESS, payload: response.data });
  } catch (error) {
    yield put({ type: SET_ERROR, payload: extractError(error) });
  }
}

function* runMutation(apiCall: () => unknown) {
  yield put({ type: SET_ACTION_PENDING, payload: true });
  yield put({ type: SET_ERROR, payload: null });
  try {
    yield call(apiCall);
    yield put({ type: CLOSE_JOB });
    yield call(fetchData);
  } catch (error) {
    yield put({ type: SET_ERROR, payload: extractError(error) });
    yield put({ type: SET_ACTION_PENDING, payload: false });
  }
}

function* requeueJobSaga(action: { payload: number }) {
  yield call(runMutation, () => api.requeueJob(action.payload));
}

function* reloadJobSaga(action: { payload: number }) {
  yield call(runMutation, () => api.reloadJob(action.payload));
}

function* destroyJobSaga(action: { payload: number }) {
  yield call(runMutation, () => api.destroyJob(action.payload));
}

function* requeueFailedSaga() {
  const { appliedQueues }: DelayedJobsState = yield selectModuleState();
  yield call(runMutation, () => api.requeueFailed(buildQueueParams(appliedQueues)));
}

function* requeuePendingSaga() {
  const { appliedQueues }: DelayedJobsState = yield selectModuleState();
  yield call(runMutation, () => api.requeuePending(buildQueueParams(appliedQueues)));
}

function* clearFailedSaga() {
  const { appliedQueues }: DelayedJobsState = yield selectModuleState();
  yield call(runMutation, () => api.clearFailed(buildQueueParams(appliedQueues)));
}

export default function* delayedJobsSagas() {
  const listen = (pattern: string, worker: (...args: never[]) => unknown) =>
    takeLatest(pattern, worker as never);

  yield listen(CHANGE_TAB, fetchData);
  yield listen(APPLY_FILTERS, fetchData);
  yield listen(CHANGE_PAGE, fetchData);
  yield listen(REFRESH, fetchData);
  yield listen(FETCH_JOB, fetchJobSaga);
  yield listen(REQUEUE_JOB, requeueJobSaga);
  yield listen(RELOAD_JOB, reloadJobSaga);
  yield listen(DESTROY_JOB, destroyJobSaga);
  yield listen(REQUEUE_FAILED, requeueFailedSaga);
  yield listen(REQUEUE_PENDING, requeuePendingSaga);
  yield listen(CLEAR_FAILED, clearFailedSaga);
}
